import {
  DatePicker,
  Flex,
  InputNumber,
  Space,
  Tooltip,
  Typography,
} from '@arkestro/arkestro-design-system';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Controller, FieldValues, useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import dayjs from 'dayjs';
import { UnitSelect } from '@components/shared/UnitSelect/UnitSelect';
import { unitPlaceholderKey } from '@components/shared/UnitSelect/unitPlaceholderKey';
import {
  DEFAULT_SCHEDULE_PERIOD_UNIT,
  deriveSchedule,
  isPastDate,
  MAX_SCHEDULE_PERIOD_BY_UNIT,
  MIN_SCHEDULE_PERIOD,
  SCHEDULE_PERIOD_UNIT_OPTIONS,
  SchedulePeriodUnit,
} from './schedulePeriod';
import styles from './PriceUpdateSchedulePanel.module.scss';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

interface PriceUpdateSchedulePanelProps {
  disableFields?: boolean;
}

const DERIVED_START_FORMAT = 'MMMM D, YYYY';

/** Blocks past dates only — Prices Due By may land on a weekend. */
const disabledPricesDueBy = (current: dayjs.Dayjs) => current && current.isBefore(dayjs().startOf('day'));

/**
 * A numeric value paired with a unit selector, rendered as one compact control.
 *
 * Bounds are validated rather than left to antd's clamp, which only fires on blur and step, so a
 * value entered under one unit could survive a switch to a shorter one.
 */
interface PeriodFieldProps {
  disableFields: boolean;
  label: string;
  testIdPrefix: string;
  unitName: string;
  valueName: string;
}

const PeriodField = ({
  disableFields, label, testIdPrefix, unitName, valueName,
}: PeriodFieldProps) => {
  const { t } = useTranslation('common');
  const { getValues, setValue } = useFormContext();
  const unit: SchedulePeriodUnit = useWatch({ name: unitName }) ?? DEFAULT_SCHEDULE_PERIOD_UNIT;
  const periodValue: number | null | undefined = useWatch({ name: valueName });

  /** Reads the unit from the form values — the Select re-renders separately from this rule. */
  const withinUnitBounds = (value: number | null, values: FieldValues) => {
    if (value == null) return true;
    const selected: SchedulePeriodUnit = values[unitName] ?? DEFAULT_SCHEDULE_PERIOD_UNIT;

    return value >= MIN_SCHEDULE_PERIOD && value <= MAX_SCHEDULE_PERIOD_BY_UNIT[selected];
  };

  /** antd reports a clamped value through onChange but leaves the input showing what was typed. */
  const clampToUnit = (next: SchedulePeriodUnit) => {
    const max = MAX_SCHEDULE_PERIOD_BY_UNIT[next];
    const current = getValues(valueName);
    if (current != null && current > max) setValue(valueName, max, { shouldValidate: true });
  };

  return (
    <Flex gap={4} vertical>
      <Flex align='center' gap={4}>
        <span className={sharedStyles.required}>*</span>
        <Typography className={sharedStyles.text}>{label}</Typography>
        <Tooltip placement='bottom' title={t('rfx_drawer.business_days_tooltip')}>
          <QuestionCircleOutlined className={styles.tooltipIcon} data-testid={`${testIdPrefix}-business-days-tooltip`} />
        </Tooltip>
      </Flex>
      <Space.Compact className={styles.periodRow}>
        <Controller
          name={valueName}
          render={({ field: { value, onChange, onBlur } }) => (
            <InputNumber
              className={styles.periodInput}
              controls={false}
              data-testid={`${testIdPrefix}-input`}
              disabled={disableFields}
              max={MAX_SCHEDULE_PERIOD_BY_UNIT[unit]}
              min={MIN_SCHEDULE_PERIOD}
              onBlur={onBlur}
              onChange={(val: number | string | null) => onChange(val == null || val === '' ? null : Number(val))}
              placeholder={t(unitPlaceholderKey(unit))}
              precision={0}
              step={1}
              value={value ?? undefined}
            />
          )}
          rules={{ required: true, validate: withinUnitBounds }}
        />
        <Controller
          name={unitName}
          render={({ field: { value, onChange } }) => (
            <UnitSelect
              dataTestId={`${testIdPrefix}-unit-select`}
              disabled={disableFields}
              onChange={(next: SchedulePeriodUnit) => {
                onChange(next);
                clampToUnit(next);
              }}
              options={SCHEDULE_PERIOD_UNIT_OPTIONS(t, periodValue)}
              value={value}
            />
          )}
        />
      </Space.Compact>
    </Flex>
  );
};

export const PriceUpdateSchedulePanel = ({ disableFields = false }: PriceUpdateSchedulePanelProps) => {
  const { t } = useTranslation('common');

  const pricesDueBy = useWatch({ name: 'pricesDueBy' });
  const initialSubmissionValue = useWatch({ name: 'initialSubmissionValue' });
  const initialSubmissionUnit = useWatch({ name: 'initialSubmissionUnit' });
  const reviewValue = useWatch({ name: 'reviewValue' });
  const reviewUnit = useWatch({ name: 'reviewUnit' });

  const { trigger } = useFormContext();

  const scheduleComplete = pricesDueBy != null && initialSubmissionValue != null && reviewValue != null;
  const schedule = deriveSchedule({
    initialSubmissionUnit,
    initialSubmissionValue,
    pricesDueBy,
    reviewUnit,
    reviewValue,
    shouldSkipWeekends: true,
  });
  const derivedStart = schedule?.startTime ?? null;
  const startsInPast = derivedStart != null && isPastDate(derivedStart);
  const doesNotFit = scheduleComplete && schedule == null;

  useEffect(() => {
    if (pricesDueBy) trigger('pricesDueBy');
  }, [pricesDueBy, initialSubmissionValue, initialSubmissionUnit, reviewValue, reviewUnit, trigger]);

  return (
    <Flex className={sharedStyles.panel} gap={16} vertical>
      <Typography className={sharedStyles.panelHeader}>{t('rfx_drawer.schedule_panel_title')}</Typography>

      <Flex gap={4} vertical>
        <Flex align='center' gap={4}>
          <span className={sharedStyles.required}>*</span>
          <Typography className={sharedStyles.text}>{t('rfx_drawer.prices_due_by_label')}</Typography>
        </Flex>
        <Controller
          name="pricesDueBy"
          render={({ field: { value, onChange } }) => (
            <DatePicker
              className={styles.input}
              dataTestId='prices-due-by-picker'
              disabled={disableFields}
              disabledDate={disabledPricesDueBy}
              format="YYYY-MM-DD hh:mm A"
              onChange={onChange}
              placeholder={t('rfx_drawer.prices_due_by_placeholder')}
              showTime={{ format: 'hh:mm A', use12Hours: true }}
              value={value}
            />
          )}
          rules={{ required: true, validate: () => !startsInPast && !doesNotFit }}
        />
      </Flex>

      <PeriodField
        disableFields={disableFields}
        label={t('rfx_drawer.initial_submission_period_label')}
        testIdPrefix='initial-submission'
        unitName='initialSubmissionUnit'
        valueName='initialSubmissionValue'
      />

      <PeriodField
        disableFields={disableFields}
        label={t('rfx_drawer.review_period_label')}
        testIdPrefix='review'
        unitName='reviewUnit'
        valueName='reviewValue'
      />

      <Flex gap={4} vertical>
        <Typography className={sharedStyles.text}>
          {t('rfx_drawer.derived_start_label')}
          {' '}
          <span
            className={startsInPast ? styles.derivedStartError : styles.derivedStart}
            data-testid='derived-start-preview'
          >
            {derivedStart ? derivedStart.format(DERIVED_START_FORMAT) : t('rfx_drawer.derived_start_incomplete')}
          </span>
        </Typography>
        {startsInPast && (
          <Typography className={styles.error} data-testid='derived-start-error'>
            {t('rfx_drawer.derived_start_in_past_error')}
          </Typography>
        )}
        {doesNotFit && (
          <Typography className={styles.error} data-testid='schedule-does-not-fit-error'>
            {t('rfx_drawer.schedule_does_not_fit_error')}
          </Typography>
        )}
        <Typography className={styles.hint}>
          {t('rfx_drawer.derived_start_hint')}
          {' '}
          {t('rfx_drawer.derived_start_weekend_hint')}
        </Typography>
      </Flex>
    </Flex>
  );
};
